import { useEffect, useState, useRef } from 'react';
import { collection, query, where, onSnapshot, addDoc, orderBy, limit, Timestamp } from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import { db } from '../lib/firebase';
import { useFamily } from '../contexts/FamilyContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePullToRefresh } from '../hooks/useTouchOptimizations';
import { getAllExperienceTypes, getExperienceType } from '../data/experienceTypes';
import ChatWidget from '../components/chat/ChatWidget';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGear, 
  faUsers, 
  faSearch, 
  faComments, 
  faGift, 
  faMoon, 
  faBook, 
  faPalette,
  faUtensils,
  faMask,
  faBookOpen
} from '@fortawesome/free-solid-svg-icons';
import { getCurrentHebrewDate } from '../lib/hebcal';
import { getCategoryImage } from '../lib/unsplash';
import { recipes, activities, stories } from '../data/content';
import WordModal from '../components/modals/WordModal';
import ShabbatPlanningModal from '../components/modals/ShabbatPlanningModal';
import ActivityModal from '../components/modals/ActivityModal';
import { getWordOfTheDay } from '../data/jewishWords';
import ExperienceCard from '../components/experiences/ExperienceCard';
import { 
  filterExperiencesByStatus, 
  sortExperiencesByDate, 
  filterExperiencesByType, 
  searchExperiences,
  groupExperiencesByTime,
  getDaysUntil,
  getExperienceStatus,
  EXPERIENCE_STATUS
} from '../utils/experiences';
import ChatInput from '../components/dashboard/ChatInput';
import RecentChats from '../components/dashboard/RecentChats';
import ConversationStarters from '../components/dashboard/ConversationStarters';
import ActiveExperience from '../components/dashboard/ActiveExperience';
import ThisWeek from '../components/dashboard/ThisWeek';
import { generateConversationStarters } from '../utils/conversationStarters';
import HebrewCalendarHeader from '../components/HebrewCalendarHeader';
import PersonalizedFeed from '../components/dashboard/PersonalizedFeed';
import HebrewCalendarCard from '../components/dashboard/HebrewCalendarCard';
import HebrewCalendarHeroV2 from '../components/dashboard/HebrewCalendarHeroV2';
import ThisWeekHorizontal from '../components/dashboard/ThisWeekHorizontal';
import ThisWeekMobileCompact from '../components/dashboard/ThisWeekMobileCompact';
import MobileHeader from '../components/navigation/MobileHeader';
import ChatSection from '../components/dashboard/ChatSection';
import { motion, AnimatePresence } from 'framer-motion';
import ExperienceCreationModal from '../components/experiences/ExperienceCreationModal';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

export default function Dashboard() {
  const [experiences, setExperiences] = useState([]);
  const [activeExperience, setActiveExperience] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showWordModal, setShowWordModal] = useState(false);
  const [showShabbatModal, setShowShabbatModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [wordOfTheDay] = useState(() => getWordOfTheDay());
  const [currentActivity, setCurrentActivity] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [communityTip, setCommunityTip] = useState(null);
  const [hebrewDate, setHebrewDate] = useState('');
  const [exploreImages, setExploreImages] = useState({});
  const [experienceForm, setExperienceForm] = useState({
    title: '',
    date: '',
    guests: '',
  });
  const [experienceTab, setExperienceTab] = useState('active'); // 'active', 'past', 'all'
  const [experienceTypeFilter, setExperienceTypeFilter] = useState('all');
  const [experienceSearchQuery, setExperienceSearchQuery] = useState('');
  const [recentConversations, setRecentConversations] = useState([]);
  const [conversationStarters, setConversationStarters] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const scrollRef = useRef(null);
  const { family, familyId } = useFamily();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Pull-to-refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    // Reload data - in a real app, you'd refresh experiences, conversations, etc.
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
    toast.success('Refreshed!');
  };

  // Track scroll for parallax effect and header visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Hide header when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setHeaderVisible(false);
      } else {
        setHeaderVisible(true);
      }
      
      lastScrollY.current = currentScrollY;
      setScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Pull-to-refresh functionality - using window for now
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    let startY = 0;
    let currentY = 0;
    let pullDistance = 0;

    const handleTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (startY === 0) return;
      if (window.scrollY > 0) {
        startY = 0;
        setPullDistance(0);
        return;
      }

      currentY = e.touches[0].clientY;
      pullDistance = currentY - startY;

      if (pullDistance > 0 && window.scrollY === 0) {
        setPullDistance(Math.min(pullDistance, 100));
        if (pullDistance > 10) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = async () => {
      if (pullDistance > 60) {
        await handleRefresh();
      }
      setPullDistance(0);
      startY = 0;
      pullDistance = 0;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [refreshing]);

  useEffect(() => {
    if (!familyId) return;

    const q = query(
      collection(db, 'projects'),
      where('familyId', '==', familyId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const experiencesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setExperiences(experiencesData);
      
      // Set the most recent or active experience (excluding past experiences)
      if (experiencesData.length > 0) {
        // Filter out past experiences
        const activeExperiences = experiencesData.filter(e => {
          const daysUntil = getDaysUntil(e);
          const status = getExperienceStatus(e);
          // Only include experiences that are not completed/past
          return status !== EXPERIENCE_STATUS.COMPLETED && (daysUntil === null || daysUntil >= 0);
        });
        
        if (activeExperiences.length > 0) {
          // Find experience with currentPhase, or use the first one
          const active = activeExperiences.find(e => e.currentPhase) || activeExperiences[0];
          setActiveExperience(active);
        } else {
          // No active experiences, set to null
          setActiveExperience(null);
        }
      }
    });

    return unsubscribe;
  }, [familyId]);

  // Load recent conversations
  useEffect(() => {
    if (!familyId) return;

    const q = query(
      collection(db, 'conversations'),
      where('familyId', '==', familyId),
      where('archived', '==', false),
      orderBy('updatedAt', 'desc'),
      limit(3)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentConversations(convs);
    }, (error) => {
      // Ignore errors for conversations (might not have index yet or permissions)
      // Only log in development to avoid console noise
      if (process.env.NODE_ENV === 'development' && error.code !== 'permission-denied') {
        console.log('Conversations query error (may need index):', error);
      }
    });

    return unsubscribe;
  }, [familyId]);

  // Generate conversation starters
  useEffect(() => {
    if (family && experiences.length >= 0) {
      const starters = generateConversationStarters(family, experiences);
      setConversationStarters(starters);
    }
  }, [family, experiences]);

  // Fetch current Hebrew date from Hebcal API
  useEffect(() => {
    const fetchHebrewDate = async () => {
      try {
        const dateInfo = await getCurrentHebrewDate();
        const now = new Date();
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = days[now.getDay()];
        setHebrewDate(`${dayName}, ${dateInfo.hebrewDate}`);
      } catch (error) {
        console.error('Error loading Hebrew date:', error);
        // Fallback
        const now = new Date();
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = days[now.getDay()];
        setHebrewDate(`${dayName}, ${now.getDate()} Shvat`);
      }
    };
    fetchHebrewDate();
  }, []);

  // Load images for explore cards on dashboard
  useEffect(() => {
    const loadExploreImages = async () => {
      try {
        // Get featured items for each category
        const featuredRecipe = recipes.find(r => r.id === 'shakshuka') || recipes[0];
        const featuredActivity = activities[0] || null;
        const featuredStory = stories[0] || null;

        const [recipeImage, activityImage, storyImage] = await Promise.all([
          getCategoryImage('recipes', featuredRecipe),
          getCategoryImage('activities', featuredActivity),
          getCategoryImage('stories', featuredStory),
        ]);

        setExploreImages({
          recipes: recipeImage,
          activities: activityImage,
          stories: storyImage,
        });
      } catch (error) {
        console.error('Error loading explore images:', error);
      }
    };

    loadExploreImages();
  }, []);

  // Listen for mobile nav chat button click
  useEffect(() => {
    const handleOpenChat = () => {
      setShowChat(true);
    };
    window.addEventListener('openChat', handleOpenChat);
    return () => window.removeEventListener('openChat', handleOpenChat);
  }, []);

  // Listen for createExperience event and location state
  useEffect(() => {
    const handleCreateExperience = (event) => {
      if (event.detail?.type) {
        setSelectedType(event.detail.type);
        // Set default title if holiday name provided
        if (event.detail.holidayName) {
          const currentYear = new Date().getFullYear();
          setExperienceForm({
            title: `${event.detail.holidayName} ${currentYear}`,
            date: '',
            guests: ''
          });
        }
        setShowCreateModal(true);
      } else {
        setShowCreateModal(true);
      }
    };
    window.addEventListener('createExperience', handleCreateExperience);
    
    return () => window.removeEventListener('createExperience', handleCreateExperience);
  }, []);

  // Check location state for openCreateModal
  useEffect(() => {
    if (location.state?.openCreateModal) {
      if (location.state.type) {
        setSelectedType(location.state.type);
      }
      if (location.state.selectedTemplate) {
        setSelectedTemplate(location.state.selectedTemplate);
      }
      setShowCreateModal(true);
      // Clear the state to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Set up weekly activity based on kids' ages
  useEffect(() => {
    if (!family?.kids || family.kids.length === 0) {
      // Default activity if no kids
      setCurrentActivity(activities.find(a => a.id === 'shabbat-blessing-cards') || activities[0]);
      return;
    }

    const kidsAges = family.kids.map(kid => parseInt(kid.age)).filter(age => !isNaN(age));
    if (kidsAges.length === 0) {
      setCurrentActivity(activities[0]);
      return;
    }

    // Find activities that match kids' ages
    const suitableActivities = activities.filter(activity => {
      const [minAge, maxAge] = activity.ageRange.split('-').map(Number);
      return kidsAges.some(age => age >= minAge && age <= maxAge);
    });

    // Pick a random suitable activity, or default to first activity
    const selectedActivity = suitableActivities.length > 0
      ? suitableActivities[Math.floor(Math.random() * suitableActivities.length)]
      : activities[0];

    setCurrentActivity(selectedActivity);
  }, [family?.kids]);

  // Fetch community tips
  useEffect(() => {
    if (!family?.onboarding) return;

    const constraints = [
      where('approved', '==', true),
      orderBy('upvotes', 'desc'),
      limit(10),
    ];

    const q = query(collection(db, 'communityTips'), ...constraints);
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tips = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter for similar families (same starting point or similar kids ages)
      const similarTips = tips.filter(tip => {
        const sameStyle = tip.familyStyle === family.onboarding.startingPoint;
        const similarAges = tip.kidsAges && family.onboarding.kids && 
          tip.kidsAges.some(age => 
            family.onboarding.kids.some(kid => Math.abs(kid.age - age) <= 2)
          );
        return sameStyle || similarAges;
      });

      // Pick a random tip from similar ones, or any tip if none match
      const selectedTip = similarTips.length > 0 
        ? similarTips[Math.floor(Math.random() * similarTips.length)]
        : tips.length > 0 
        ? tips[Math.floor(Math.random() * tips.length)]
        : null;

      setCommunityTip(selectedTip);
    });

    return unsubscribe;
  }, [family?.onboarding]);

  const getUserName = () => {
    return user?.displayName?.split(' ')[0] || family?.onboarding?.yourName || 'there';
  };

  const getFamilyStyleLabel = (style) => {
    const labels = {
      'just-beginning': 'Just Beginning',
      'holidays-only': 'Holidays Only',
      'dabbling': 'Dabbling',
      'pretty-active': 'Pretty Active',
    };
    return labels[style] || style;
  };

  const getPhaseLabel = (phase) => {
    const phases = {
      'exposure': 'Explore',
      'discussion': 'Decide',
      'execution': 'Execute',
    };
    return phases[phase] || phase;
  };

  const calculateProgress = (project) => {
    if (!project?.decisions) return 0;
    const decisions = Object.values(project.decisions);
    if (decisions.length === 0) return 0;
    const completed = decisions.filter(d => d.status === 'locked').length;
    return Math.round((completed / decisions.length) * 100);
  };

  const progress = activeExperience ? calculateProgress(activeExperience) : 0;

  // Handle chat input send
  const handleChatSend = (message, linkedExperience) => {
    setShowChat(true);
    // ChatWidget will handle the actual sending
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('chatInput', { 
        detail: { message, experience: linkedExperience } 
      }));
    }, 100);
  };

  // Handle conversation starter selection
  const handleStarterSelect = (prompt) => {
    setShowChat(true);
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('chatInput', { 
        detail: { message: prompt, experience: null } 
      }));
    }, 100);
  };


  const generateInviteToken = () => {
    // Generate a secure random token
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const handleGenerateInvite = async () => {
    if (!familyId) {
      toast.error('Please complete onboarding first');
      return;
    }

    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      const token = generateInviteToken();
      const inviteData = {
        familyId: familyId,
        email: inviteEmail.trim().toLowerCase(),
        token: token,
        createdBy: user.uid,
        createdAt: new Date(),
        used: false,
        usedBy: null,
        usedAt: null,
      };

      await addDoc(collection(db, 'invites'), inviteData);

      // Generate shareable link
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/accept-invite/${token}`;
      setInviteLink(link);
      
      toast.success('Invite link generated!');
    } catch (error) {
      console.error('Error creating invite:', error);
      toast.error('Failed to create invite. Please try again.');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success('Link copied to clipboard!');
  };

  const firstName = user?.displayName?.split(' ')[0] || 'there';

  return (
    <>
    <div 
      ref={scrollRef}
      className="min-h-screen bg-gradient-to-b from-background to-white pt-14 pb-20 md:pt-0 md:pb-0 flex flex-col overflow-y-auto momentum-scroll"
    >
      {/* Mobile: Floating Header - rendered by MobileHeader component itself */}

      {/* Pull-to-refresh indicator */}
      <AnimatePresence>
        {pullDistance > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ 
              opacity: Math.min(pullDistance / 60, 1),
              y: pullDistance / 3
            }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 md:hidden"
          >
            <div className="bg-white rounded-full p-3 shadow-lg">
              <svg 
                className={`w-6 h-6 text-primary ${refreshing ? 'animate-spin' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                />
              </svg>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hebrew Calendar Hero V2 - Responsive (handles both mobile and desktop) */}
      <HebrewCalendarHeroV2 />

      {/* Mobile: Compact This Week */}
      <div className="md:hidden">
        <ThisWeekMobileCompact />
      </div>

      {/* Desktop: This Week - Horizontal Sub-nav */}
      <div className="hidden md:block">
        <ThisWeekHorizontal
          onShabbatClick={() => setShowShabbatModal(true)}
          onWordClick={() => setShowWordModal(true)}
          onActivityClick={(activity) => {
            setCurrentActivity(activity);
            setShowActivityModal(true);
          }}
          kidsAges={family?.kids?.map(k => k.age) || []}
        />
        </div>

      {/* Centered Chat Section - Mobile optimized */}
      <div className="flex-1 flex items-center justify-center py-8 md:py-20 px-4 md:px-8">
        <div className="max-w-2xl mx-auto w-full">
          <ChatSection 
            experiences={experiences}
            currentExperience={activeExperience}
          />
        </div>
      </div>

      {/* Full Width Feed - Mobile optimized spacing */}
      <div className="px-4 md:px-8 pb-16 md:pb-24">
        <PersonalizedFeed 
          family={family}
          experiences={experiences}
          conversations={recentConversations}
        />
      </div>

      {/* Legacy Active Experience Card (Hidden - keeping for reference) */}
      <div className="hidden">
          {/* Active Project Card */}
          {activeExperience ? (
            <div 
              className="bg-white rounded-card p-6 sm:p-9 relative overflow-hidden min-h-[300px] sm:min-h-[420px] flex flex-col animate-fadeIn shadow-card hover:shadow-card-hover transition-shadow"
              style={{ animationDelay: '0.15s' }}
            >
              <div className="inline-block text-[10px] sm:text-[11px] font-semibold tracking-wider uppercase bg-surface px-2 sm:px-3 py-1 sm:py-1.5 rounded-[20px] mb-3 sm:mb-4 w-fit text-text-secondary">
                Your Next Celebration
              </div>

              <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
                {['exposure', 'discussion', 'execution'].map((phase) => (
                  <div
                    key={phase}
                    className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-xl text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide ${
                      activeExperience.currentPhase === phase
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-secondary'
                    }`}
                  >
                    {getPhaseLabel(phase)}
                  </div>
                ))}
              </div>

              <div className="font-sans text-xl sm:text-2xl md:text-3xl font-semibold mb-2 sm:mb-3 leading-tight text-text-primary tracking-tight">
                {activeExperience.title || 'Passover Seder'}
              </div>
              <div className="text-sm sm:text-base text-text-secondary mb-4 sm:mb-6">
                {activeExperience.guests ? `Hosting ${activeExperience.guests} people` : 'Hosting 12 people'} • {activeExperience.date ? (activeExperience.date.toDate ? activeExperience.date.toDate().toLocaleDateString() : new Date(activeExperience.date).toLocaleDateString()) : 'April 12 at 6pm'}
        </div>

              {activeExperience.currentPhase === 'discussion' && activeExperience.decisions && (
                <div className="bg-surface rounded-xl p-4 sm:p-5 mb-4 sm:mb-5">
                  <div className="text-xs sm:text-[13px] font-semibold mb-3 sm:mb-4 text-text-primary">
                    Making Decisions ({progress}% Complete)
                  </div>
                  <div className="flex flex-col gap-2 sm:gap-3">
                    {Object.entries(activeExperience.decisions).slice(0, 4).map(([key, decision]) => (
                      <div key={key} className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                        <div
                          className={`w-4 h-4 sm:w-5 sm:h-5 border rounded-md flex-shrink-0 flex items-center justify-center ${
                            decision.status === 'locked'
                              ? 'bg-primary border-primary'
                              : 'border-border'
                          }`}
                        >
                          {decision.status === 'locked' && (
                            <span className="text-white text-xs sm:text-sm font-bold">✓</span>
                          )}
                        </div>
                        <span className="break-words text-text-primary">{decision.value || key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-auto flex flex-col sm:flex-row gap-2 sm:gap-3 relative">
                <button
                  onClick={() => navigate(`/experience/${activeExperience.id}`)}
                  className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold bg-white text-primary border border-border hover:border-primary hover:shadow-button-hover transition-all"
                >
                  Continue Planning
                </button>
                <button className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover">
                  Chat About It
              </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-card shadow-card p-6 sm:p-9 min-h-[300px] sm:min-h-[420px] flex items-center justify-center animate-fadeIn" style={{ animationDelay: '0.15s' }}>
              <div className="text-center px-4">
                <div className="mb-3 sm:mb-4">
                  <FontAwesomeIcon icon={faGift} className="text-primary text-4xl" />
                </div>
                <p className="text-sm sm:text-base text-text-secondary mb-4 sm:mb-6">
                  No active experiences yet. Ready to plan something meaningful?
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-primary text-white font-semibold py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
                >
                  Create Your First Experience
                </button>
              </div>
            </div>
          )}

          {/* Sidebar */}
          <div className="flex flex-col gap-4 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
            {/* AI Chat Widget */}
            <button
              onClick={() => setShowChat(true)}
              className="bg-white rounded-card p-6 cursor-pointer hover:shadow-card-hover transition-all text-left w-full border border-border shadow-card"
            >
              <div className="mb-3">
                <FontAwesomeIcon icon={faComments} className="text-primary text-3xl" />
              </div>
              <div className="text-base font-semibold text-text-primary mb-1.5">Need Help?</div>
              <div className="text-[13px] text-text-secondary">
                Ask me anything about planning, recipes, or Jewish traditions
              </div>
            </button>

            {/* This Week */}
            <div className="bg-white rounded-card shadow-card border border-border p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="text-base font-semibold text-text-primary">This Week</div>
                <div className="text-[13px] text-primary cursor-pointer font-medium hover:underline">
                  View All
                </div>
              </div>
              <div className="flex flex-col gap-3.5">
                <button
                  onClick={() => setShowShabbatModal(true)}
                  className="flex gap-3 items-start p-3 bg-surface rounded-[10px] cursor-pointer hover:bg-[#F0F0F0] transition-colors w-full text-left"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <FontAwesomeIcon icon={faMoon} className="text-primary text-lg" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-text-primary mb-0.5">
                      Shabbat Dinner Tomorrow
                    </div>
                    <div className="text-xs text-text-secondary">30-min menu ready</div>
                  </div>
                </button>
                <button
                  onClick={() => setShowWordModal(true)}
                  className="flex gap-3 items-start p-3 bg-surface rounded-[10px] cursor-pointer hover:bg-[#F0F0F0] transition-colors w-full text-left"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <FontAwesomeIcon icon={faBook} className="text-primary text-lg" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-text-primary mb-0.5">
                      Word of the Day: {wordOfTheDay?.transliteration || 'Shalom'}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {family?.kids && family.kids.length > 0
                        ? `Teach ${family.kids.map(k => k.name).join(' & ')}`
                        : 'Learn a new word'
                      }
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => currentActivity && setShowActivityModal(true)}
                  className="flex gap-3 items-start p-3 bg-surface rounded-[10px] cursor-pointer hover:bg-[#F0F0F0] transition-colors w-full text-left"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <FontAwesomeIcon icon={faPalette} className="text-primary text-lg" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-text-primary mb-0.5">
                      {currentActivity?.title || 'Weekly Activity'}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {currentActivity?.category || 'Activity'} • {currentActivity?.time || '30 min'}
                    </div>
                  </div>
                </button>
              </div>
        </div>

            {/* Recent Chats */}
            {recentConversations.length > 0 && (
              <div className="bg-white rounded-card shadow-card border border-border p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-base font-semibold text-text-primary">Recent Chats</div>
                  <button
                    onClick={() => navigate('/chats')}
                    className="text-[13px] text-primary cursor-pointer font-medium hover:underline"
                  >
                    View All
                  </button>
                </div>
                <div className="space-y-3">
                  {recentConversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('openChat', { detail: { conversationId: conv.id } }));
                        setShowChat(true);
                      }}
                      className="w-full text-left bg-surface p-3 rounded-lg hover:bg-[#F0F0F0] transition-colors"
                    >
                      <div className="text-sm font-semibold text-text-primary mb-1 line-clamp-1">
                        {conv.title || 'New Conversation'}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {conv.updatedAt
                          ? formatDistanceToNow(
                              conv.updatedAt?.toDate 
                                ? conv.updatedAt.toDate() 
                                : new Date(conv.updatedAt),
                              { addSuffix: true }
                            )
                          : 'Just now'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Community Highlight */}
            <div className="bg-white rounded-card shadow-card border border-border p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="text-base font-semibold text-text-primary">From Families Like Yours</div>
                <button
                  onClick={() => navigate('/community')}
                  className="text-[13px] text-primary cursor-pointer font-medium hover:underline"
                >
                  View All
                </button>
              </div>
              {communityTip ? (
                <div className="bg-surface p-5 rounded-xl border border-border">
                  <div className="text-[11px] font-semibold text-primary uppercase tracking-wide mb-3">
                    {getExperienceType(communityTip.projectType)?.name || 'Community Tip'}
                  </div>
                  <div className="text-sm text-text-primary leading-relaxed mb-3">
                    "{communityTip.content}"
                  </div>
                  <div className="flex items-center gap-2 text-[13px] text-text-secondary">
                    <span>{getFamilyStyleLabel(communityTip.familyStyle)}</span>
                    {communityTip.kidsAges && communityTip.kidsAges.length > 0 && (
                      <>
                        <span>•</span>
                        <span>Ages {communityTip.kidsAges.join(', ')}</span>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-surface p-5 rounded-xl border border-border text-sm text-text-secondary text-center">
                  No tips yet. Be the first to share!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invite Partner Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 sm:p-6" onClick={() => {
          setShowInviteModal(false);
          setInviteEmail('');
          setInviteLink('');
        }}>
          <div className="bg-white rounded-card max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-6 md:p-8">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h2 className="font-sans text-lg sm:text-xl md:text-2xl font-semibold text-text-primary tracking-tight">Invite Your Partner</h2>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail('');
                    setInviteLink('');
                  }}
                  className="text-2xl sm:text-3xl text-text-secondary hover:text-text-primary transition-colors flex-shrink-0 ml-4"
                >
                  ×
                </button>
              </div>

              {!inviteLink ? (
                <div>
                  <p className="text-text-secondary mb-4">
                    Invite your partner to collaborate on planning. They'll be able to see and contribute to all your projects.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-text-primary mb-2">
                        Partner's Email
                      </label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:border-primary transition-all"
                        placeholder="partner@example.com"
                        onKeyPress={(e) => e.key === 'Enter' && handleGenerateInvite()}
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => {
                          setShowInviteModal(false);
                          setInviteEmail('');
                        }}
                        className="flex-1 px-6 py-3 rounded-xl font-semibold bg-white border border-border text-text-secondary hover:border-primary hover:text-primary transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleGenerateInvite}
                        className="flex-1 px-6 py-3 rounded-lg font-semibold bg-primary text-white hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover"
                      >
                        Generate Link
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-text-secondary mb-4">
                    Share this link with your partner. They'll sign in and be automatically added to your family.
                  </p>
                  <div className="space-y-4">
                    <div className="bg-surface rounded-xl p-4 border border-border">
                      <div className="text-xs text-text-secondary mb-2">Invite Link</div>
                      <div className="text-sm text-text-primary break-all font-mono bg-white p-3 rounded-lg border border-border">
                        {inviteLink}
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={handleCopyLink}
                        className="flex-1 px-6 py-3 rounded-lg font-semibold bg-primary text-white hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover"
                      >
                        📋 Copy Link
                      </button>
                      <button
                        onClick={() => {
                          setShowInviteModal(false);
                          setInviteEmail('');
                          setInviteLink('');
                        }}
                        className="px-6 py-3 rounded-xl font-semibold bg-white border border-border text-text-secondary hover:border-primary hover:text-primary transition-all"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              )}
        </div>
      </div>
    </div>
      )}

      {/* Create Experience Modal */}
      <ExperienceCreationModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedType(null);
          setSelectedTemplate(null);
          setExperienceForm({ title: '', date: '', guests: '' });
        }}
        selectedTemplate={selectedTemplate}
      />

      {/* Chat Widget */}
      <ChatWidget 
        isOpen={showChat} 
        onClose={setShowChat}
        experiences={experiences}
        currentExperience={activeExperience}
      />

      {/* Word Modal */}
      <WordModal 
        isOpen={showWordModal} 
        onClose={() => setShowWordModal(false)}
        wordOfTheDay={wordOfTheDay}
      />

      {/* Shabbat Planning Modal */}
      <ShabbatPlanningModal 
        isOpen={showShabbatModal} 
        onClose={() => setShowShabbatModal(false)}
        onCreateExperience={(typeId) => {
          setSelectedType(typeId);
          setShowShabbatModal(false);
          setShowCreateModal(true);
        }}
      />

      {/* Activity Modal */}
      <ActivityModal 
        isOpen={showActivityModal} 
        onClose={() => setShowActivityModal(false)}
        activity={currentActivity}
      />

      {/* Shabbat Planning Modal */}
      <ShabbatPlanningModal 
        isOpen={showShabbatModal} 
        onClose={() => setShowShabbatModal(false)}
        onCreateExperience={(typeId) => {
          setSelectedType(typeId);
          setShowShabbatModal(false);
          setShowCreateModal(true);
        }}
      />

      {/* Activity Modal */}
      <ActivityModal 
        isOpen={showActivityModal} 
        onClose={() => setShowActivityModal(false)}
        activity={currentActivity}
      />
    </>
  );
}
